import { useQuery } from "@tanstack/react-query";
import type {
  AvailabilityDto,
  CategoryDto,
  ImportJobDto,
  MediaDto,
  MenuDraftDto,
  MenuSummaryDto,
  ProductDto,
  ProfileDto,
  PublishPreviewDto,
  PublishResultDto,
} from "../../shared/api-types";
import type { Appearance, AppearanceDto } from "../../shared/appearance";
import type { LabelDraft, LabelSave } from "../../shared/food-labels";
import type { FoodDto } from "../../shared/foods";
import type { PublicProfile } from "../../shared/profile";
import type { RecipeDto, RecipeSave } from "../../shared/recipes";
import type { MenuSnapshot } from "../../shared/snapshot";
import { api } from "./client";

// Server state for the dashboard. Every mutation invalidates exactly what it can change, so lists,
// drafts, previews and the pending indicator always reflect the server (no optimistic fake state).

export const keys = {
  menus: (rid: string) => ["menus", rid] as const,
  draft: (rid: string, mid: string) => ["draft", rid, mid] as const,
  preview: (rid: string, mid: string) => ["preview", rid, mid] as const,
  published: (rid: string, mid: string) => ["published", rid, mid] as const,
  profile: (rid: string) => ["profile", rid] as const,
  appearance: (rid: string) => ["appearance", rid] as const,
  imports: (rid: string) => ["imports", rid] as const,
};

const base = (rid: string) => `/restaurants/${rid}`;
const menuUrl = (rid: string, mid: string) => `${base(rid)}/menus/${mid}`;

export const useMenus = (rid: string) =>
  useQuery({
    queryKey: keys.menus(rid),
    queryFn: () => api<{ menus: MenuSummaryDto[] }>("GET", `${base(rid)}/menus`).then((r) => r.menus),
  });

export const useDraft = (rid: string, mid: string | null) =>
  useQuery({
    queryKey: keys.draft(rid, mid ?? ""),
    queryFn: () => api<MenuDraftDto>("GET", menuUrl(rid, mid ?? "")),
    enabled: !!mid,
  });

export const usePublishPreview = (rid: string, mid: string | null, enabled = true) =>
  useQuery({
    queryKey: keys.preview(rid, mid ?? ""),
    queryFn: () => api<PublishPreviewDto>("GET", `${menuUrl(rid, mid ?? "")}/publish-preview`),
    enabled: !!mid && enabled,
  });

export const usePublished = (rid: string, mid: string | null, enabled: boolean) =>
  useQuery({
    queryKey: keys.published(rid, mid ?? ""),
    queryFn: () =>
      api<{ snapshot: MenuSnapshot }>("GET", `${menuUrl(rid, mid ?? "")}/published`).then((r) => r.snapshot),
    enabled: !!mid && enabled,
  });

export const useProfile = (rid: string) =>
  useQuery({ queryKey: keys.profile(rid), queryFn: () => api<ProfileDto>("GET", `${base(rid)}/profile`) });

export const useAppearance = (rid: string) =>
  useQuery({
    queryKey: keys.appearance(rid),
    queryFn: () => api<AppearanceDto>("GET", `${base(rid)}/appearance`),
  });

/** "Aspect și personalizare": every change is saved to the draft; publish/discard confirm the revision seen. */
export function appearanceApi(rid: string) {
  const url = `${base(rid)}/appearance`;
  return {
    save: (draft: Appearance) => api<AppearanceDto>("PUT", url, draft),
    publish: (revision: number) => api<AppearanceDto>("POST", `${url}/publish`, { revision }),
    discard: (revision: number) => api<AppearanceDto>("POST", `${url}/discard`, { revision }),
    upload: (kind: "logo" | "cover", image: Blob) =>
      api<{ media: MediaDto }>("POST", `${base(rid)}/media?kind=${kind}`, image).then((r) => r.media),
  };
}

export function menuApi(rid: string) {
  const m = (mid: string) => menuUrl(rid, mid);
  return {
    create: (name: string) =>
      api<{ menu: MenuSummaryDto }>("POST", `${base(rid)}/menus`, { name }).then((r) => r.menu),
    rename: (mid: string, name: string) => api<{ menu: MenuSummaryDto }>("PATCH", m(mid), { name }),
    setFrozen: (mid: string, pid: string, frozen: boolean) =>
      api<{ product: ProductDto; draftRevision: number }>("PUT", `${m(mid)}/products/${pid}/frozen`, {
        frozen,
      }),
    setSchedule: (mid: string, schedule: string) =>
      api<{ menu: MenuSummaryDto }>("PUT", `${m(mid)}/schedule`, { schedule }),
    duplicate: (mid: string) =>
      api<{ menu: MenuSummaryDto }>("POST", `${m(mid)}/duplicate`).then((r) => r.menu),
    archive: (mid: string) => api("POST", `${m(mid)}/archive`),
    unarchive: (mid: string) => api("POST", `${m(mid)}/unarchive`),
    remove: (mid: string) => api("DELETE", m(mid)),
    unpublish: (mid: string) => api("POST", `${m(mid)}/unpublish`),
    /** `allergenNotice`: the restaurant acknowledged the allergen notice shown before publishing (DEC-058). */
    publish: (mid: string, draftRevision: number, key: string, allergenNotice = false) =>
      api<PublishResultDto>(
        "POST",
        `${m(mid)}/publish`,
        allergenNotice ? { draftRevision, allergenNotice: true } : { draftRevision },
        { "Idempotency-Key": key },
      ),
    discard: (mid: string, draftRevision: number) => api("POST", `${m(mid)}/discard`, { draftRevision }),
    createCategory: (mid: string, input: { name: string; translation?: { lang: "en"; name: string } }) =>
      api<{ category: CategoryDto }>("POST", `${m(mid)}/categories`, input),
    updateCategory: (
      mid: string,
      cid: string,
      input: { name: string; translation?: { lang: "en"; name: string } },
    ) => api<{ category: CategoryDto }>("PATCH", `${m(mid)}/categories/${cid}`, input),
    reorderCategories: (mid: string, order: string[]) =>
      api("POST", `${m(mid)}/categories/reorder`, { order }),
    deleteCategory: (
      mid: string,
      cid: string,
      input: { mode: "move"; targetCategoryId: string } | { mode: "delete" },
    ) => api("DELETE", `${m(mid)}/categories/${cid}`, input),
    createProduct: (mid: string, input: unknown) =>
      api<{ product: ProductDto }>("POST", `${m(mid)}/products`, input).then((r) => r.product),
    updateProduct: (mid: string, pid: string, input: unknown) =>
      api<{ product: ProductDto }>("PUT", `${m(mid)}/products/${pid}`, input).then((r) => r.product),
    updatePrice: (mid: string, pid: string, price: string) =>
      api<{ product: ProductDto }>("PATCH", `${m(mid)}/products/${pid}/price`, { price }).then(
        (r) => r.product,
      ),
    duplicateProduct: (mid: string, pid: string) =>
      api<{ product: ProductDto }>("POST", `${m(mid)}/products/${pid}/duplicate`).then((r) => r.product),
    deleteProduct: (mid: string, pid: string) => api("DELETE", `${m(mid)}/products/${pid}`),
    restoreProduct: (mid: string, pid: string) => api("POST", `${m(mid)}/products/${pid}/restore`),
    setAvailability: (pid: string, available: boolean) =>
      api<AvailabilityDto>("PUT", `${base(rid)}/availability/${pid}`, { available }),
    saveProfile: (input: PublicProfile) => api<ProfileDto>("PUT", `${base(rid)}/profile`, input),
    publishProfile: (revision: number) =>
      api<ProfileDto>("POST", `${base(rid)}/profile/publish`, { revision }),
    discardProfile: (revision: number) =>
      api<ProfileDto>("POST", `${base(rid)}/profile/discard`, { revision }),
    // "Verifică" (Lot 3 step 4)
    confirmProducts: (mid: string, draftRevision: number) =>
      api<{ confirmed: number; draftRevision: number }>("POST", `${m(mid)}/review/confirm-products`, {
        draftRevision,
      }),
    keepDuplicate: (mid: string, pid: string) => api("POST", `${m(mid)}/products/${pid}/review/keep`),
    resolvePortion: (mid: string, pid: string, portion: string) =>
      api("POST", `${m(mid)}/products/${pid}/review/portion`, { portion }),
    resolveName: (mid: string, pid: string, name: string) =>
      api("POST", `${m(mid)}/products/${pid}/review/name`, { name }),
    confirmTranslations: (mid: string, lang: string, draftRevision: number) =>
      api<{ confirmed: number; draftRevision: number }>("POST", `${m(mid)}/translations/${lang}/confirm`, {
        draftRevision,
      }),
    uploadPhoto: (photo: Blob) =>
      api<{ media: MediaDto }>("POST", `${base(rid)}/media`, photo).then((r) => r.media),
  };
}

const ACTIVE_IMPORT = new Set(["uploading", "queued", "processing"]);

/** Latest imports; polls every 1.5 s while one is running (real job states, no simulated progress). */
export const useImports = (rid: string) =>
  useQuery({
    queryKey: keys.imports(rid),
    queryFn: () => api<{ imports: ImportJobDto[] }>("GET", `${base(rid)}/imports`).then((r) => r.imports),
    // Also while a finished job's draft is being prepared (ready, no draft yet, no error).
    refetchInterval: (q) =>
      q.state.data?.some(
        (j) => ACTIVE_IMPORT.has(j.status) || (j.status === "ready" && !j.menuId && !j.error),
      )
        ? 1500
        : false,
  });

export function importApi(rid: string) {
  const url = (jid: string) => `${base(rid)}/imports/${jid}`;
  const one = (r: { import: ImportJobDto }) => r.import;
  return {
    /** `menuId`: re-import into that menu (new / changed / missing products) instead of a new menu. */
    create: (languages: string[], menuId?: string) =>
      api<{ import: ImportJobDto }>("POST", `${base(rid)}/imports`, {
        languages,
        ...(menuId ? { menuId } : {}),
      }).then(one),
    /**
     * A page prepared in the browser: its image, then its crops, in one request (see routes/imports.ts). With
     * `retakeOf`, the new photo of that page of a finished import ("Refotografiază pagina").
     */
    uploadPage: (
      jid: string,
      page: {
        position: number;
        name: string;
        page: number;
        image: Blob;
        views: Blob[];
        /** Text layer of a PDF page (JSON), kept for the benchmark's offline comparison. */
        text?: Blob | null;
        sourceSha256?: string;
        prep?: unknown;
      },
      signal?: AbortSignal,
      retakeOf?: string,
    ) =>
      api<{ import: ImportJobDto }>(
        "POST",
        retakeOf ? `${url(jid)}/files/${retakeOf}/retake` : `${url(jid)}/files`,
        new Blob([page.image, ...page.views, ...(page.text ? [page.text] : [])], { type: page.image.type }),
        {
          "X-File-Name": encodeURIComponent(page.name),
          "X-Position": String(page.position),
          "X-Page": String(page.page),
          ...(page.views.length ? { "X-Page-Views": page.views.map((v) => v.size).join(",") } : {}),
          ...(page.text ? { "X-Page-Text": String(page.text.size) } : {}),
          ...(page.sourceSha256 ? { "X-Source-Sha256": page.sourceSha256 } : {}),
          ...(page.prep ? { "X-Page-Prep": encodeURIComponent(JSON.stringify(page.prep)) } : {}),
        },
        signal,
      ).then(one),
    submit: (jid: string, prepMs?: number) =>
      api<{ import: ImportJobDto }>(
        "POST",
        `${url(jid)}/submit`,
        prepMs === undefined ? undefined : { prepMs },
      ).then(one),
    retry: (jid: string) => api<{ import: ImportJobDto }>("POST", `${url(jid)}/retry`).then(one),
    reread: (jid: string) => api<{ import: ImportJobDto }>("POST", `${url(jid)}/reread`).then(one),
    cancel: (jid: string) => api<{ import: ImportJobDto }>("POST", `${url(jid)}/cancel`).then(one),
  };
}

/** Member-only URL of an uploaded photo (drafts included). Customers get published photos under /m/:slug. */
export const mediaUrl = (rid: string, id: string) => `/api${base(rid)}/media/${id}`;

/** Opens the server-rendered draft preview in a new tab (prototype "Previzualizează"). */
export function openPreview(rid: string, mid?: string | null) {
  window.open(`/api/restaurants/${rid}/preview${mid ? `?menu=${mid}` : ""}`, "_blank", "noopener");
}

/** Ingredient and product database (docs/BAZA_INGREDIENTE.md): search, label photos, recipes. */
export function foodApi(rid: string) {
  const f = `${base(rid)}/foods`;
  return {
    search: (q: string) =>
      api<{ results: FoodDto[] }>("GET", `${f}/search?q=${encodeURIComponent(q)}`).then((r) => r.results),
    uploadLabelPhoto: (photo: Blob) => api<{ id: string }>("POST", `${f}/labels/photos`, photo),
    readLabel: (photoIds: string[]) =>
      api<{ draft: LabelDraft }>("POST", `${f}/labels/read`, { photoIds }).then((r) => r.draft),
    saveLabel: (input: LabelSave) =>
      api<{ product: FoodDto }>("POST", `${f}/labels`, input).then((r) => r.product),
    recipe: (mid: string, pid: string) =>
      api<{ recipe: RecipeDto }>("GET", `${menuUrl(rid, mid)}/products/${pid}/recipe`).then((r) => r.recipe),
    saveRecipe: (mid: string, pid: string, input: RecipeSave) =>
      api<{ recipe: RecipeDto }>("PUT", `${menuUrl(rid, mid)}/products/${pid}/recipe`, input).then(
        (r) => r.recipe,
      ),
  };
}
